import * as React from 'react'
import { Link } from 'react-router-dom'
import { Button } from '../../components/ui/button'
import { cn } from '../../lib/utils'
import { COMPACT_ACTION } from '../IngestPage/shared/compact'
import { tabularConfigBuildPath, tabularJobPath } from '../TabularIngestJobPage/route'
import type { ViewBanner } from './util/configView'
import { formatWhen, reasonText } from './util/formatMeta'

const TONE: Record<ViewBanner['kind'], string> = {
  draft_ready: 'border-st-warn/40 bg-st-warn-bg',
  building: 'border-st-info/40 bg-st-info-bg',
  saving: 'border-st-info/40 bg-st-info-bg',
  draft_saved: 'border-st-run/40 bg-st-run-bg',
  draft_rejected: 'border-line bg-muted',
  draft_superseded: 'border-line bg-muted'
}

/** What a saved version's page says about a build: waiting, running, saved, rejected, or sent back. */
export function DraftBanner({
  name,
  banner,
  navState,
  onRetryExampleIngest,
  busy
}: {
  name: string
  banner: ViewBanner
  /** `location.state` to carry to the build's page, so its Back (and Reject) keep the origin. */
  navState?: unknown
  /** Start the saved build's example run again after it failed to start. */
  onRetryExampleIngest?: () => void
  busy?: boolean
}) {
  const { build } = banner
  const parentJobId = 'parentJobId' in build.reason ? build.reason.parentJobId : undefined
  return (
    <div
      role="status"
      data-testid={`banner-${banner.kind}`}
      className={cn(
        'flex min-w-0 flex-wrap items-center gap-x-3 gap-y-1.5 rounded-lg border px-3 py-2 text-xs leading-5',
        TONE[banner.kind]
      )}
    >
      <div className="min-w-0 flex-1">
        {banner.kind === 'draft_ready' ? (
          <>
            Draft v{build.proposedVersion} is waiting for review — {reasonText(build.reason)}
            <FromJob parentJobId={parentJobId} />
          </>
        ) : banner.kind === 'building' ? (
          <>
            Builder is drafting v{build.proposedVersion} — {reasonText(build.reason)}
            <FromJob parentJobId={parentJobId} />
          </>
        ) : banner.kind === 'saving' ? (
          <>Draft v{build.proposedVersion} is being saved as a version…</>
        ) : banner.kind === 'draft_saved' ? (
          <>
            This draft was saved as v{banner.version} by {banner.by} at {formatWhen(banner.at)}.
            <ExampleIngest ingest={banner.exampleIngest} />
          </>
        ) : banner.kind === 'draft_superseded' ? (
          <>
            Revised by {banner.by} at {formatWhen(banner.at)}
            {banner.note ? <>: “{banner.note}”</> : '.'}
          </>
        ) : (
          <>
            The draft was rejected by {banner.by} at {formatWhen(banner.at)}
            {banner.note ? <>: “{banner.note}”</> : '.'}
          </>
        )}
      </div>
      {banner.kind === 'draft_ready' ? (
        <Button asChild size="sm" className={COMPACT_ACTION}>
          <Link to={tabularConfigBuildPath(name, build.jobId)} state={navState}>
            Review draft
          </Link>
        </Button>
      ) : banner.kind === 'building' ? (
        <Button asChild size="sm" variant="outline" className={COMPACT_ACTION}>
          <Link to={tabularConfigBuildPath(name, build.jobId)} state={navState}>
            Watch build
          </Link>
        </Button>
      ) : banner.kind === 'draft_superseded' && banner.supersededBy ? (
        <Button asChild size="sm" variant="outline" className={COMPACT_ACTION}>
          <Link to={tabularConfigBuildPath(name, banner.supersededBy)} state={navState}>
            Open the new build →
          </Link>
        </Button>
      ) : banner.kind === 'draft_saved' && banner.exampleIngest?.error && onRetryExampleIngest ? (
        <Button
          size="sm"
          variant="outline"
          className={COMPACT_ACTION}
          disabled={busy}
          onClick={onRetryExampleIngest}
        >
          Retry example ingest
        </Button>
      ) : null}
    </div>
  )
}

/** Where the saved build's example files went: the ingest run the save started, why none did, or that none was asked for. */
function ExampleIngest({
  ingest
}: {
  ingest?: { jobIds: string[]; error?: string; skipped?: boolean }
}) {
  if (!ingest) return null
  return (
    <span data-testid="example-ingest">
      {ingest.skipped ? (
        <> The example files were not ingested — the config was saved on its own.</>
      ) : null}
      {ingest.jobIds.length ? (
        <>
          {' '}
          The example files are being ingested:{' '}
          {ingest.jobIds.map((jobId, index) => (
            <React.Fragment key={jobId}>
              {index ? ', ' : ''}
              <Link className="underline" to={tabularJobPath(jobId)}>
                job {jobId}
              </Link>
            </React.Fragment>
          ))}
          .
        </>
      ) : null}
      {ingest.error ? (
        <>
          {' '}
          <span className="text-st-down">
            {ingest.jobIds.length
              ? 'Some example files could not be ingested'
              : 'The example files could not be ingested'}
            : {ingest.error}
          </span>
        </>
      ) : null}
    </span>
  )
}

function FromJob({ parentJobId }: { parentJobId?: string }) {
  if (!parentJobId) return null
  return (
    <>
      {' '}
      ·{' '}
      <Link className="underline" to={tabularJobPath(parentJobId)}>
        from job {parentJobId}
      </Link>
    </>
  )
}
